import './MyPlanPage.scss'

import { CuiIcon, CuiTag } from 'front-lib'
import React, { FC, useCallback, useEffect, useState } from 'react'
import SettingsCard, { SettingSection } from '../../components/SettingsCard'
import {
  cleanPausePlanStatus,
  getLastSkips,
  getSubscriptionPlans,
  getUserData
} from 'src/redux-api-bridge'
import {
  getExpireDateSelector,
  getSubscriptionPlanSelector,
  getUserSelector,
  myAccountIsLoadingSelector
} from 'src/modules/MyAccount/selectors'
import {
  planIsLoading as planIsLoadingSelector,
  planIsPaused as planIsPausedSelector
} from './selectors'
import { useDispatch, useSelector } from 'react-redux'
import Analytics from '../../utils/analytics'

import { ConfirmationPlanChangeModalDesktop } from './components/MyFlexModePlan/components/confirmationPlanChange/ConfirmationPlanChangeModalDesktop'
import { ConfirmationPlanChangeModalMobile } from './components/MyFlexModePlan/components/confirmationPlanChange/ConfirmationPlanChangeModalMobile'
import { EconomicPlanModal } from './components/EconomicPlanModal/EconomicPlanModal'
import Header from 'src/components/Header/Header'
import Info from '../../assets/icons/info.png'
import Loader from 'src/common/Loader'
import { MyFlexModePlan } from './components/MyFlexModePlan/MyFlexModePlan'
import { RoutePaths } from 'src/routes/routePaths'
import View from '../../modules/MyAccount/components/View'
import { formatNumber } from '../../utils/utils'
import moment from 'moment'
import tracking from '../../shared/tracking'
import { useHistory } from 'react-router-dom'
import { useMobileDevice } from '../../hooks/useMobileDevice'
import { useTranslation } from 'react-i18next'
import {
  isEconomicPlanSelector,
  isFlexPlanSelector
} from 'src/redux-api-bridge/selectors'
import { PausePlanSettings } from './components/PausePlanSettings/PausePlanSettings'
import { useQueryParams } from '../../hooks/useQueryParams'

export const MyPlanPage: FC = () => {
  const { t } = useTranslation()

  const [showEconomicPlanModal, setShowEconomicPlanModal] = useState(false)
  const [
    showConfirmationPlanChangeModal,
    setShowConfirmationPlanChangeModal
  ] = useState(false)

  const user = useSelector(getUserSelector)
  const subscriptionPlan = useSelector(getSubscriptionPlanSelector)
  const isEconomicPlan = useSelector(isEconomicPlanSelector)
  const isFlexPlan = useSelector(isFlexPlanSelector)
  const expireDate = useSelector(getExpireDateSelector)
  const myAccountIsLoading = useSelector(myAccountIsLoadingSelector)
  const planIsLoading = useSelector(planIsLoadingSelector)
  const planIsPaused = useSelector(planIsPausedSelector)
  const query = useQueryParams()

  const successChangePlan = useSelector<any>(
    state => state?.api?.setSubscriptionPlanStatus?.succeeded
  )
  const dispatch = useDispatch()
  const isMobileDevice = useMobileDevice()
  const history = useHistory()

  const deliveriesPerWeek = subscriptionPlan?.deliveries ?? 1
  const mealsPerDelivery = subscriptionPlan?.mealsPerDelivery ?? 1
  const price = subscriptionPlan?.price ?? ''
  const mealsPerWeek = mealsPerDelivery * deliveriesPerWeek
  const pricePerMeal = formatNumber(price / mealsPerWeek)
  const firstName = user?.firstname ?? ''
  const lastName = user?.lastname ?? ''
  const email = user?.email ?? ''
  const userFullName = [
    ...(firstName ? [firstName.trim()] : []),
    ...(lastName ? [lastName.trim()] : [])
  ].join(' ')
  const userEmail = email ? email.trim() : ''

  useEffect(() => {
    if (!user) {
      dispatch(getUserData())
    }
  }, [user, dispatch])

  useEffect(() => {
    dispatch(getSubscriptionPlans())
    dispatch(cleanPausePlanStatus())
    dispatch(getLastSkips({ user: null, fromLastDays: 0 }))
  }, [dispatch])

  useEffect(() => {
    if (showConfirmationPlanChangeModal && successChangePlan) {
      dispatch(getSubscriptionPlans())
    }
  }, [dispatch, successChangePlan, showConfirmationPlanChangeModal])

  const getNamePlan = useCallback(
    mealsPerWeek => {
      return `${mealsPerWeek} ${
        mealsPerWeek > 1 ? t('pages.my_plan.meals') : t('pages.my_plan.meal')
      }`
    },
    [t]
  )

  useEffect(() => {
    if (query.get('weeks') !== null && mealsPerWeek > 1) {
      Analytics.track('Pause-Plan', {
        plan_name: getNamePlan(mealsPerWeek),
        is_paused: true,
        pause_duration: `${query.get('weeks')} Weeks`,
        origin: 'brightback',
        reason: transformReason(query.get('reason'))
      })
    }
  }, [getNamePlan, mealsPerWeek, query])

  function onChangePlan() {
    tracking.track('Change Plan')
    history.push(RoutePaths.CHANGE_PLAN)
  }

  const transformReason = brightbackReason => {
    const brightbackReasonsMapper = {
      i_am_not_happy_with_the_service: "i'm not happy with the service",
      ill_try_out_another_service: 'I will try out other service',
      pricing_dont_see_value: 'I am not happy with the service',
      i_just_need_a_break: 'I just need a break',
      i_need_to_cut_down_my_budge: 'I need to cut down my budget',
      other_: 'I’ll be out of town'
    }

    for (const key in brightbackReasonsMapper) {
      const regex = new RegExp(`${key}_\\d+`)
      if (regex.test(brightbackReason)) {
        return brightbackReasonsMapper[key]
      }
    }
    return brightbackReason
  }

  function getPlanSubtitle(pricePerMeal, deliveriesPerWeek) {
    return `$${pricePerMeal} per Meal  ·  ${deliveriesPerWeek} ${
      deliveriesPerWeek > 1
        ? t('pages.my_plan.deliveries')
        : t('pages.my_plan.delivery')
    } `
  }

  function isPlanWithDeliveryFee(plan) {
    return typeof plan.deliveryFee === 'number'
  }

  function getEconomicPlanTag() {
    if (subscriptionPlan?.segmentId === 4) {
      return t('pages.my_plan.economic_meal_plan')
    }

    if ([6, 11].includes(subscriptionPlan?.segmentId)) {
      return t('pages.my_plan.economic_meal_plan_8.5')
    }

    if ([8, 12].includes(subscriptionPlan?.segmentId)) {
      if (mealsPerDelivery === 4) {
        return t('pages.my_plan.economic_meal_plan_9')
      }
      return t('pages.my_plan.economic_meal_plan_8.5')
    }
  }

  if (myAccountIsLoading) {
    return <Loader />
  }

  if (isFlexPlan) {
    return (
      <MyFlexModePlan
        setShowConfirmationPlanChangeModal={setShowConfirmationPlanChangeModal}
      />
    )
  }

  const ConfirmationPlanChangeModal = isMobileDevice
    ? ConfirmationPlanChangeModalMobile
    : ConfirmationPlanChangeModalDesktop

  return (
    <>
      <Header />

      {showEconomicPlanModal && (
        <EconomicPlanModal
          showEconomicPlanModal={showEconomicPlanModal}
          setShowEconomicPlanModal={setShowEconomicPlanModal}
        />
      )}

      {showConfirmationPlanChangeModal && (
        <ConfirmationPlanChangeModal
          showConfirmationPlanChangeModal={showConfirmationPlanChangeModal}
          setShowConfirmationPlanChangeModal={
            setShowConfirmationPlanChangeModal
          }
        />
      )}

      <div className="cookunity__account_settings_container">
        {!isMobileDevice && (
          <div className="cookunity__account_settings_header">
            <>
              <h1 className="title">{t('pages.my_plan.my_plan')}</h1>
              <p className="subtitle">
                <strong>{userFullName},</strong> {userEmail} |{' '}
                <strong>
                  {`${mealsPerWeek} ${
                    mealsPerWeek > 1
                      ? t('pages.my_plan.meals')
                      : t('pages.my_plan.meal')
                  }, ${deliveriesPerWeek} ${
                    deliveriesPerWeek > 1
                      ? t('pages.my_plan.deliveries')
                      : t('pages.my_plan.delivery')
                  } · `}
                </strong>
              </p>
            </>
            {isEconomicPlan && (
              <div className="plan-tag-container">
                <CuiTag color="blue">{getEconomicPlanTag()}</CuiTag>
                {expireDate && (
                  <div className="expire-date">
                    {`${t('pages.my_plan.expiresOn')} ${moment(
                      expireDate
                    ).format('MM/DD/YY')}`}
                  </div>
                )}
                <div className="plan-info">
                  <img
                    src={Info}
                    className="cui-meal-info"
                    alt="info"
                    onClick={() =>
                      setShowEconomicPlanModal(showModal => !showModal)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="cookunity__account_settings">
          <View hideBack={true}>
            <SettingsCard
              hideTitle={!isMobileDevice}
              title={t('pages.my_plan.my_plan')}
              isLoading={planIsLoading}>
              {isMobileDevice && isEconomicPlan && (
                <div className="plan-tag-container">
                  <CuiTag color="blue">{getEconomicPlanTag()}</CuiTag>
                  {expireDate && (
                    <div className="expire-date">
                      {`${t('pages.my_plan.expiresOn')} ${moment(
                        expireDate
                      ).format('MM/DD/YY')}`}
                    </div>
                  )}
                  <div className="plan-info">
                    <img
                      src={Info}
                      className="cui-meal-info"
                      alt="info"
                      onClick={() =>
                        setShowEconomicPlanModal(showModal => !showModal)
                      }
                    />
                  </div>
                </div>
              )}
              <SettingSection
                className="cookunity__my_plan_section"
                title={
                  isPlanWithDeliveryFee(subscriptionPlan) ? (
                    <span className="title_new_plan_meals">
                      {getNamePlan(mealsPerWeek)}
                      <span className="title_new_plan_meals_week">{` / ${t(
                        'pages.my_plan.week'
                      )}`}</span>
                    </span>
                  ) : (
                    <span className="title_new_plan_meals">
                      {getNamePlan(mealsPerWeek)}
                      <span className="title_new_plan_meals_week">{` / ${t(
                        'pages.my_plan.weekly'
                      )}`}</span>
                    </span>
                  )
                }
                subtitle={
                  isPlanWithDeliveryFee(subscriptionPlan) ? (
                    <span>
                      <span className="subtitle_new_plan_price">
                        {`$${pricePerMeal} ${t('pages.my_plan.per_meal')}`}
                      </span>
                      <span className="subtitle_new_plan_delivery">
                        {` + ${t('pages.my_plan.taxes_and_fees')}`}
                      </span>
                    </span>
                  ) : (
                    getPlanSubtitle(pricePerMeal, deliveriesPerWeek)
                  )
                }
                withActionButton
                buttonLabel={
                  <>
                    <CuiIcon name="sync" /> {t('pages.my_plan.change_plan')}
                  </>
                }
                buttonClassName={
                  planIsPaused
                    ? 'cookunity__my_plan_section-cancel-plan-secondary'
                    : ''
                }
                onActionClick={onChangePlan}
              />
              <PausePlanSettings />
            </SettingsCard>
          </View>
        </div>
      </div>
    </>
  )
}
